import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Import data
import { swimlaneData } from '../../../data/workflowData';

// Import custom hooks
import useCardRefs from './hooks/useCardRefs';
import useCardPositions from './hooks/useCardPositions';

// Import components
import TabPanel from './components/TabPanel';
import WorkflowTabs from './components/WorkflowTabs';
import Swimlane from './components/Swimlane';
import DependencyLines from './components/DependencyLines';

const ProjectWorkflow = () => {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);
  const workflowRef = useRef(null);
  const [hoveredLinkedCardId, setHoveredLinkedCardId] = useState(null);
  const [hoveredChipTargetCardId, setHoveredChipTargetCardId] = useState(null);
  const [highlightedDependency, setHighlightedDependency] = useState(null); // Track highlighted dependency relationship
  const highlightedCardId = ''; // Assuming this is intentionally static or managed elsewhere

  // Get all cards from all swimlanes for position calculation
  const allCards = useMemo(() => {
    let cards = [];
    swimlaneData.forEach(lane => {
      if (lane.cards && Array.isArray(lane.cards)) {
        cards = [...cards, ...lane.cards];
      }
    });
    return cards;
  }, []);

  // For rendering the current tab's cards
  const allCardsForCurrentTab = useMemo(() => 
    swimlaneData[currentTab]?.cards || [], 
    [currentTab]
  );

  // Use custom hooks for card references and positions
  const { cardRefs, refsAreAttached } = useCardRefs(allCards, currentTab);
  const cardPositions = useCardPositions(
    workflowRef, 
    cardRefs, 
    allCards, 
    allCardsForCurrentTab, 
    refsAreAttached, 
    currentTab
  );

  const getCardInfoByTitle = useCallback((titleToFind) => {
    // First try exact match
    for (const lane of swimlaneData) {
      const card = lane.cards.find(c => c.title === titleToFind);
      if (card) {
        const laneColor = lane.backgroundColor || '#ffffff'; // Default to white if no lane color
        const cardBorder = card.borderColor || theme.palette.divider; // Default border color
        return {
          cardId: card.id,
          swimlaneColor: laneColor,
          cardBorderColor: cardBorder,
        };
      }
    }
    
    // If no exact match, try case-insensitive match
    const lowerTitleToFind = titleToFind.toLowerCase();
    for (const lane of swimlaneData) {
      const card = lane.cards.find(c => c.title.toLowerCase() === lowerTitleToFind);
      if (card) {
        const laneColor = lane.backgroundColor || '#ffffff';
        const cardBorder = card.borderColor || theme.palette.divider;
        return {
          cardId: card.id,
          swimlaneColor: laneColor,
          cardBorderColor: cardBorder,
        };
      }
    }
    
    // If still no match, try matching by ID (in case title was mistakenly used as ID)
    for (const lane of swimlaneData) {
      const card = lane.cards.find(c => c.id === titleToFind);
      if (card) {
        const laneColor = lane.backgroundColor || '#ffffff';
        const cardBorder = card.borderColor || theme.palette.divider;
        return {
          cardId: card.id,
          swimlaneColor: laneColor,
          cardBorderColor: cardBorder,
        };
      }
    }
    
    return { cardId: null, swimlaneColor: '#ffffff', cardBorderColor: theme.palette.divider };
  }, [theme.palette.divider]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <Paper elevation={0} sx={{ p: 0, mt: 0, backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 2, width: '100%', position: 'relative', overflow: 'hidden' }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 0, pt: 2, px: 2, pb: 0 }}>Project Workflow</Typography>
      
      <WorkflowTabs 
        currentTab={currentTab}
        onTabChange={handleTabChange}
      />
      
      <Box sx={{ position: 'relative' }}>
        <TabPanel value={currentTab} index={0}>
          <Box 
            ref={workflowRef}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflowX: 'auto', // Enable horizontal scrolling
              maxWidth: '100%', // Prevent extending beyond container
              p: 2,
              pt: 0,
              pb: 1, // Reduced bottom padding
              minHeight: 420, // Slightly increased height
              width: '100%',
              position: 'relative',
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.05)',
                borderRadius: 10,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.15)',
                borderRadius: 10,
              },
            }}
          >
            {/* Render dependency lines first (behind cards) */}
            {Object.keys(cardPositions).length > 0 && (
              <DependencyLines 
                swimlaneData={swimlaneData}
                cardPositions={cardPositions}
                getCardInfoByTitle={getCardInfoByTitle}
                highlightedDependency={highlightedDependency}
              />
            )}
            
            {/* Render cards on top of dependency lines */}
            {swimlaneData.map(lane => (
              <Swimlane 
                key={lane.title}
                lane={lane}
                cardRefs={cardRefs}
                hoveredLinkedCardId={hoveredLinkedCardId}
                onCardHover={setHoveredLinkedCardId}
                highlightedCardId={highlightedCardId}
                swimlaneData={swimlaneData}
                hoveredChipTargetCardId={hoveredChipTargetCardId}
                setHoveredChipTargetCardId={setHoveredChipTargetCardId}
                highlightedDependency={highlightedDependency}
                setHighlightedDependency={setHighlightedDependency}
              />
            ))}
          </Box>
        </TabPanel>
        
        <TabPanel value={currentTab} index={1}>Design Process Content</TabPanel>
        <TabPanel value={currentTab} index={2}>Verification Content</TabPanel>
        <TabPanel value={currentTab} index={3}>Validation Content</TabPanel>
        <TabPanel value={currentTab} index={4}>Design Transfer Content</TabPanel>
      </Box>
    </Paper>
  );
};

export default ProjectWorkflow;
